import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';

// Create notification context
export const NotificationContext = createContext();

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const eventName = 'nixia:notify';

  const DEFAULT_DURATION_MS = 5000;
  const MESSAGE_DURATION_MS = 10000;
  
  // Generate a unique ID for each notification
  const generateId = () => `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Backend error `detail`/messages can arrive as a plain string or as a
  // structured object ({message, error_code, ...}) - never render the object
  // directly (React error #31: objects are not valid as a child).
  const toDisplayText = (value, fallback = 'Une erreur est survenue.') => {
    if (value == null) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return value.message || fallback;
    return String(value);
  };

  // Add a notification
  const addNotification = useCallback((message, type = 'success', autoHide = true, payload = null, durationMs = null) => {
    const id = generateId();
    const notification = {
      id,
      message: toDisplayText(message),
      type, // 'success', 'error', 'info', 'warning'
      payload,
      timestamp: Date.now()
    };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-hide notifications
    if (autoHide) {
      const timeoutMs = Number.isFinite(Number(durationMs)) && Number(durationMs) > 0
        ? Number(durationMs)
        : DEFAULT_DURATION_MS;
      setTimeout(() => {
        removeNotification(id);
      }, timeoutMs);
    }
    
    return id;
  }, []);
  
  // Les messages entrants sont regroupes dans UNE seule bulle, qui se met a jour
  // au lieu d'en empiler une par message : en production, plusieurs clients qui
  // ecrivent en meme temps saturaient l'ecran de l'agent (retour de recette 5.1).
  const messageGroupRef = useRef({ id: null, count: 0, conversationIds: new Set(), timer: null });

  const resetMessageGroup = useCallback(() => {
    const group = messageGroupRef.current;
    if (group.timer) clearTimeout(group.timer);
    messageGroupRef.current = { id: null, count: 0, conversationIds: new Set(), timer: null };
  }, []);

  // Remove a notification by ID
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
    // La bulle groupee vient d'etre fermee (clic, navigation) : le prochain
    // message doit en ouvrir une neuve, pas reprendre le compteur precedent.
    if (messageGroupRef.current.id === id) resetMessageGroup();
  }, [resetMessageGroup]);
  
  // Add different types of notifications
  const showSuccess = useCallback((message) => {
    return addNotification(message, 'success');
  }, [addNotification]);
  
  const showError = useCallback((message) => {
    return addNotification(message, 'error');
  }, [addNotification]);
  
  const showInfo = useCallback((message) => {
    return addNotification(message, 'info');
  }, [addNotification]);
  
  const showWarning = useCallback((message) => {
    return addNotification(message, 'warning');
  }, [addNotification]);
  
  // Generic showNotification function that routes to the appropriate method
  const showNotification = useCallback((message, type = 'success') => {
    return addNotification(message, type);
  }, [addNotification]);

  const addMessageNotification = useCallback((payload, autoHide = true) => {
    const group = messageGroupRef.current;
    const conversationId = payload?.conversation?.id;

    const scheduleHide = (id) => {
      if (group.timer) clearTimeout(group.timer);
      if (!autoHide) return;
      // Le delai repart a chaque nouveau message : la bulle reste visible tant
      // que ca discute, puis disparait une fois le calme revenu.
      group.timer = setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
        resetMessageGroup();
      }, MESSAGE_DURATION_MS);
    };

    if (conversationId) group.conversationIds.add(conversationId);

    if (group.id) {
      group.count += 1;
      const grouped = {
        ...payload,
        groupCount: group.count,
        groupConversations: group.conversationIds.size,
      };
      setNotifications(prev => prev.map(n => (
        n.id === group.id ? { ...n, payload: grouped, timestamp: Date.now() } : n
      )));
      scheduleHide(group.id);
      return group.id;
    }

    const id = generateId();
    group.id = id;
    group.count = 1;
    setNotifications(prev => [...prev, {
      id,
      message: '',
      type: 'message',
      payload: { ...payload, groupCount: 1, groupConversations: group.conversationIds.size || 1 },
      timestamp: Date.now(),
    }]);
    scheduleHide(id);
    return id;
  }, [resetMessageGroup]);
  
  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (!event || !event.detail) {
        return;
      }
      const { message, type = 'info', autoHide = true, payload = null, durationMs = null } = event.detail;
      if (!message) {
        return;
      }
      addNotification(message, type, autoHide, payload, durationMs);
    };

    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [addNotification]);
  
  const value = useMemo(
    () => ({
      notifications,
      addNotification,
      addMessageNotification,
      removeNotification,
      showSuccess,
      showError,
      showInfo,
      showWarning,
      showNotification,
      clearAllNotifications
    }),
    [
      notifications,
      addNotification,
      addMessageNotification,
      removeNotification,
      showSuccess,
      showError,
      showInfo,
      showWarning,
      showNotification,
      clearAllNotifications
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
